import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import {
  MailIcon,
  LockIcon,
  UserIcon,
  PhoneIcon,
  BriefcaseIcon,
  EyeIcon,
  EyeOffIcon,
} from "./AuthIcons";
import { authService, getAuthErrorMessage } from "../../services/authService";
import { validateEmail, validatePassword } from "../../utils/validators";
import Toast from "../../components/Common/Toast";

const ROLES = ["Admin", "Manager", "Segment"];

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    fname: "",
    email: "",
    mobile: "",
    role: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});

  const set = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    const next: Record<string, string> = {};
    if (form.fname.trim().length < 2) next.fname = "Enter your full name";
    if (!form.email.trim()) next.email = "Email is required";
    else if (!validateEmail(form.email)) next.email = "Enter a valid email address";
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.mobile.trim()))
      next.mobile = "Enter a valid mobile number";
    if (!form.role) next.role = "Select a role";
    if (!form.password) next.password = "Password is required";
    else if (!validatePassword(form.password))
      next.password =
        "Min 8 chars with uppercase, lowercase, number & special character";
    if (form.confirmPassword !== form.password)
      next.confirmPassword = "Passwords do not match";
    setErrs(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await authService.register({
        fname: form.fname.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        role: form.role,
        password: form.password,
      });
      navigate("/signin", {
        replace: true,
        state: { notice: "Account created successfully. Please sign in." },
      });
    } catch (err: any) {
      setToast({ type: "error", message: getAuthErrorMessage(err, "Could not create account") });
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <p className="auth-eyebrow">Get started</p>
      <h2>Create your account</h2>
      <p className="auth-subtitle">Set up your workspace access in a few steps.</p>

      <form onSubmit={handleSubmit} noValidate>
        <div className={`auth-field ${errs.fname ? "has-error" : ""}`}>
          <label htmlFor="fname">Full name</label>
          <div className="auth-input-wrap">
            <span className="auth-icon">
              <UserIcon />
            </span>
            <input
              id="fname"
              name="fname"
              type="text"
              placeholder="Jane Doe"
              value={form.fname}
              onChange={set("fname")}
              autoComplete="name"
            />
          </div>
          {errs.fname && <span className="auth-field-error">{errs.fname}</span>}
        </div>

        <div className={`auth-field ${errs.email ? "has-error" : ""}`}>
          <label htmlFor="email">Email address</label>
          <div className="auth-input-wrap">
            <span className="auth-icon">
              <MailIcon />
            </span>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="you@company.com"
              value={form.email}
              onChange={set("email")}
              autoComplete="email"
            />
          </div>
          {errs.email && <span className="auth-field-error">{errs.email}</span>}
        </div>

        <div className="auth-row">
          <div className={`auth-field ${errs.mobile ? "has-error" : ""}`}>
            <label htmlFor="mobile">Mobile</label>
            <div className="auth-input-wrap">
              <span className="auth-icon">
                <PhoneIcon />
              </span>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                placeholder="+1 555 000 1234"
                value={form.mobile}
                onChange={set("mobile")}
                autoComplete="tel"
              />
            </div>
            {errs.mobile && <span className="auth-field-error">{errs.mobile}</span>}
          </div>

          <div className={`auth-field ${errs.role ? "has-error" : ""}`}>
            <label htmlFor="role">Role</label>
            <div className="auth-input-wrap">
              <span className="auth-icon">
                <BriefcaseIcon />
              </span>
              <select id="role" value={form.role} onChange={set("role")}>
                <option value="" disabled>
                  Select role
                </option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            {errs.role && <span className="auth-field-error">{errs.role}</span>}
          </div>
        </div>

        <div className={`auth-field ${errs.password ? "has-error" : ""}`}>
          <label htmlFor="password">Password</label>
          <div className="auth-input-wrap">
            <span className="auth-icon">
              <LockIcon />
            </span>
            <input
              id="password"
              name="password"
              className="has-eye"
              type={showPassword ? "text" : "password"}
              placeholder="Create a strong password"
              value={form.password}
              onChange={set("password")}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-eye"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {errs.password && <span className="auth-field-error">{errs.password}</span>}
        </div>

        <div className={`auth-field ${errs.confirmPassword ? "has-error" : ""}`}>
          <label htmlFor="confirmPassword">Confirm password</label>
          <div className="auth-input-wrap">
            <span className="auth-icon">
              <LockIcon />
            </span>
            <input
              id="confirmPassword"
              name="confirmPassword"
              className="has-eye"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter password"
              value={form.confirmPassword}
              onChange={set("confirmPassword")}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-eye"
              onClick={() => setShowConfirmPassword((s) => !s)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {errs.confirmPassword && (
            <span className="auth-field-error">{errs.confirmPassword}</span>
          )}
        </div>

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading && <span className="auth-spinner" />}
          {loading ? "Creating account..." : "Create account"}
        </button>
      </form>

      <p className="auth-foot">
        Already have an account? <Link to="/signin">Sign in</Link>
      </p>

      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </AuthLayout>
  );
};

export default SignUpPage;
